import { Button, Modal, Spin } from 'antd'
import filesize from 'filesize'
import React from 'react'

import { TRANSCODING_EXTENSION } from '../../dux/constants/assets'
import { rehostAssetUrl } from '../../dux/modules/utils'

/*
 * Describes the original file from asset state.
 *
 * Prefers the extension over the raw mime type -- "MOV" reads better than
 * "video/quicktime" -- falling back to the mime type when an asset has no
 * extension recorded.
 */
const describeOriginal = (asset) => {
  if (!asset) return ''

  const type = asset.extension
    ? asset.extension.toUpperCase()
    : asset.file_type || ''
  const size = asset.size ? filesize(asset.size) : null

  return size ? `${type} · ${size}` : type
}

/*
 * The transcoded mp4's own url, built the way downloadAsset builds it: the
 * asset url up to "active", with the transcoded source appended.
 *
 * rehostAssetUrl points it at our own origin, which also makes the HEAD below
 * same-origin -- a cross-origin HEAD would not expose Content-Length without
 * the right CORS headers.
 */
const transcodedUrl = (asset) => {
  const source =
    asset &&
    asset.videoSrc &&
    asset.videoSrc.sources &&
    asset.videoSrc.sources[0]
  if (!source || !asset.url) return null

  const base = rehostAssetUrl(asset.url)
  const activeAt = base.indexOf('active')
  if (activeAt === -1) return null

  return base.substring(0, activeAt).concat(source)
}

class DownloadModal extends React.Component {
  state = { transcodedSize: undefined, sizeUnavailable: false }

  componentDidMount() {
    this.fetchTranscodedSize()
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.modal.modalProps.asset !== this.props.modal.modalProps.asset
    ) {
      this.fetchTranscodedSize()
    }
  }

  /*
   * The transcoded size is not in asset state -- only the original's is
   * recorded, at upload -- so it has to be asked of the object itself. A HEAD
   * gets Content-Length without pulling the file.
   *
   * A failure here is not worth surfacing: the choice still works without a
   * size, so the option renders with just its type rather than blocking or
   * erroring the dialog.
   */
  fetchTranscodedSize() {
    const url = transcodedUrl(this.props.modal.modalProps.asset)

    if (!url) {
      this.setState({ transcodedSize: null, sizeUnavailable: true })
      return
    }

    this.setState({ transcodedSize: undefined, sizeUnavailable: false })

    fetch(url, { method: 'HEAD' })
      .then((response) => {
        const length = response.headers.get('content-length')
        if (!response.ok || !length) throw new Error('no content-length')
        this.setState({
          transcodedSize: Number(length),
          sizeUnavailable: false,
        })
      })
      .catch(() =>
        this.setState({ transcodedSize: null, sizeUnavailable: true }),
      )
  }

  renderTranscodedDetail() {
    const { transcodedSize, sizeUnavailable } = this.state
    const type = TRANSCODING_EXTENSION.toUpperCase()

    if (transcodedSize === undefined && !sizeUnavailable) {
      return (
        <span>
          {type} · <Spin size="small" />
        </span>
      )
    }

    return transcodedSize ? `${type} · ${filesize(transcodedSize)}` : type
  }

  render() {
    const { modal, modalActions, appState } = this.props
    const { asset, title, originalHandler, transcodeHandler } = modal.modalProps

    const footer = (
      <div>
        <Button onClick={originalHandler}>Original</Button>
        <Button onClick={transcodeHandler}>Transcoded</Button>
        <Button onClick={modalActions.closeModal}>Cancel</Button>
      </div>
    )

    return (
      <Modal
        title={title}
        open={modal.modalOpen}
        confirmLoading={appState.asyncPending}
        maskClosable={false}
        closable={false}
        footer={footer}
      >
        <div className="modal__text">
          This video has been transcoded to MP4 format. You can download either
          the original file or the transcoded version.
        </div>
        <div className="modal__text">
          <div>
            <strong>Original:</strong> {describeOriginal(asset)}
          </div>
          <div>
            <strong>Transcoded:</strong> {this.renderTranscodedDetail()}
          </div>
        </div>
      </Modal>
    )
  }
}

export default DownloadModal
